/**
 * Camunda 7.18 Decision Definition REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/decision-definition/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  DecisionDefinition,
  DecisionDefinitionQuery,
  EvaluateDecisionDto,
  DecisionResult,
  CountResult,
} from '../types';

export class DecisionService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/decision-definition';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get decision definitions
   */
  async getList(query?: DecisionDefinitionQuery): Promise<DecisionDefinition[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<DecisionDefinition[]>(`${this.basePath}${params}`);
  }

  /**
   * Get decision definitions count
   */
  async getCount(query?: DecisionDefinitionQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get decision definition by ID
   */
  async getById(id: string): Promise<DecisionDefinition> {
    return this.client.get<DecisionDefinition>(`${this.basePath}/${id}`);
  }

  /**
   * Get decision definition by key
   */
  async getByKey(key: string): Promise<DecisionDefinition> {
    return this.client.get<DecisionDefinition>(`${this.basePath}/key/${key}`);
  }

  /**
   * Get decision definition by key and tenant
   */
  async getByKeyAndTenant(key: string, tenantId: string): Promise<DecisionDefinition> {
    return this.client.get<DecisionDefinition>(`${this.basePath}/key/${key}/tenant-id/${tenantId}`);
  }

  // ============================================================================
  // DMN XML
  // ============================================================================

  /**
   * Get DMN XML by ID
   */
  async getXmlById(id: string): Promise<{ id: string; dmnXml: string }> {
    return this.client.get<{ id: string; dmnXml: string }>(`${this.basePath}/${id}/xml`);
  }

  /**
   * Get DMN XML by key
   */
  async getXmlByKey(key: string): Promise<{ id: string; dmnXml: string }> {
    return this.client.get<{ id: string; dmnXml: string }>(`${this.basePath}/key/${key}/xml`);
  }

  /**
   * Get DMN XML by key and tenant
   */
  async getXmlByKeyAndTenant(key: string, tenantId: string): Promise<{ id: string; dmnXml: string }> {
    return this.client.get<{ id: string; dmnXml: string }>(
      `${this.basePath}/key/${key}/tenant-id/${tenantId}/xml`
    );
  }

  // ============================================================================
  // Diagram
  // ============================================================================

  /**
   * Get decision diagram by ID
   */
  async getDiagramById(id: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/diagram`);
  }

  /**
   * Get decision diagram by key
   */
  async getDiagramByKey(key: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/key/${key}/diagram`);
  }

  // ============================================================================
  // Evaluate Decision
  // ============================================================================

  /**
   * Evaluate decision by ID
   */
  async evaluateById(id: string, dto?: EvaluateDecisionDto): Promise<DecisionResult[]> {
    return this.client.post<DecisionResult[]>(`${this.basePath}/${id}/evaluate`, dto || {});
  }

  /**
   * Evaluate decision by key
   */
  async evaluateByKey(key: string, dto?: EvaluateDecisionDto): Promise<DecisionResult[]> {
    return this.client.post<DecisionResult[]>(`${this.basePath}/key/${key}/evaluate`, dto || {});
  }

  /**
   * Evaluate decision by key and tenant
   */
  async evaluateByKeyAndTenant(
    key: string,
    tenantId: string,
    dto?: EvaluateDecisionDto
  ): Promise<DecisionResult[]> {
    return this.client.post<DecisionResult[]>(
      `${this.basePath}/key/${key}/tenant-id/${tenantId}/evaluate`,
      dto || {}
    );
  }

  // ============================================================================
  // History Time to Live
  // ============================================================================

  /**
   * Update history time to live by ID
   */
  async updateHistoryTimeToLiveById(id: string, historyTimeToLive: number | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/history-time-to-live`, {
      historyTimeToLive,
    });
  }

  /**
   * Update history time to live by key
   */
  async updateHistoryTimeToLiveByKey(key: string, historyTimeToLive: number | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/key/${key}/history-time-to-live`, {
      historyTimeToLive,
    });
  }

  // ============================================================================
  // Decision Requirements Definition
  // ============================================================================

  /**
   * Get decision requirements definitions
   */
  async getDecisionRequirementsDefinitions(query?: {
    decisionRequirementsDefinitionId?: string;
    decisionRequirementsDefinitionIdIn?: string[];
    name?: string;
    nameLike?: string;
    deploymentId?: string;
    key?: string;
    keyLike?: string;
    category?: string;
    categoryLike?: string;
    version?: number;
    latestVersion?: boolean;
    resourceName?: string;
    resourceNameLike?: string;
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    includeDecisionRequirementsDefinitionsWithoutTenantId?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<any[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<any[]>(`/engine-rest/decision-requirements-definition${params}`);
  }

  /**
   * Get decision requirements definition by ID
   */
  async getDecisionRequirementsDefinitionById(id: string): Promise<any> {
    return this.client.get<any>(`/engine-rest/decision-requirements-definition/${id}`);
  }

  /**
   * Get decision requirements definition by key
   */
  async getDecisionRequirementsDefinitionByKey(key: string): Promise<any> {
    return this.client.get<any>(`/engine-rest/decision-requirements-definition/key/${key}`);
  }

  /**
   * Get decision requirements definition XML by ID
   */
  async getDecisionRequirementsDefinitionXmlById(id: string): Promise<{ id: string; dmnXml: string }> {
    return this.client.get<{ id: string; dmnXml: string }>(
      `/engine-rest/decision-requirements-definition/${id}/xml`
    );
  }

  /**
   * Get decision requirements definition diagram by ID
   */
  async getDecisionRequirementsDefinitionDiagramById(id: string): Promise<Blob> {
    return this.client.getBlob(`/engine-rest/decision-requirements-definition/${id}/diagram`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: Record<string, any>): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let decisionServiceInstance: DecisionService | null = null;

export function getDecisionService(): DecisionService {
  if (!decisionServiceInstance) {
    decisionServiceInstance = new DecisionService();
  }
  return decisionServiceInstance;
}

export default DecisionService;
